// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, forkJoin, Observable, of } from "rxjs"
import { Action } from "redux"
import { DateTime } from "luxon"
import { catchError, filter, mergeMap } from "rxjs/operators"
import * as TimelineActions from "../actions"
import * as UserContextActions from "../../user-context/actions"
import { PostTimeRequest, PostTimeResponse, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import { StatusCode } from "grpc-web"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { isGrpcError } from "../../../lib/rpcutils"
import { SiteId } from "../../user-context/slice"
import { t } from "@lingui/macro"
import { buildPostTimeRequest } from "../postTime"
import { SiteRef } from "../../../generated/wisetime/type/site_ref_pb"
import { showSnackBar } from "../../snackbar/actions"

type PostTimeRpc = (request: PostTimeRequest) => Observable<PostTimeResponse>

// Handle time posting.
export const postTime = (postTimeRpc: PostTimeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.postTime.match),
      mergeMap(({ payload }) =>
        postTimeGroups(
          payload.timeGroups,
          payload.currentSiteRef,
          payload.roundingPreferenceSec,
          postTimeRpc,
        ).pipe(
          mergeMap(([response]) => {
            const timeGroupsIds = payload.timeGroups.map(timeGroup => timeGroup.id)
            const postedAt = DateTime.now().toSeconds().toString()
            return concat(
              of(TimelineSlice.removeActiveTimeGroupsIds({
                timeGroupsIds,
              })),
              of(TimelineAdditionalUiSlice.addTimeGroupIdsInPendingProgress({
                postedAt,
                timeGroupsIds,
                postedToSiteId: payload.currentSiteRef.id,
              })),
              of(TimelineAdditionalUiSlice.resetIsPostingTime()),
              of(TimelineAdditionalUiSlice.handlePageConfetti(
                response?.getRemainingUserUnpostedTime()?.getSeconds() || 0
              )),
            )
          }),
          catchError(e => handleError(e, payload.currentSiteRef?.id)),
        ),
      )
    )

const postTimeGroups = (
  timeGroups: TimeGroup.AsObject[],
  currentSiteRef: SiteRef.AsObject,
  roundingPreferenceSec: number,
  postTimeRpc: PostTimeRpc,
): Observable<PostTimeResponse[]> =>
  forkJoin(timeGroups.map(timeGroup => postTimeRpc(buildPostTimeRequest(timeGroup, currentSiteRef, roundingPreferenceSec))))

const postTimeRpc = (request: PostTimeRequest): Observable<PostTimeResponse> =>
  grpc(() => timelineClient.postTime(request, null))

const handleError = (e: unknown, siteId: SiteId | undefined): Observable<Action> => {
  if (isGrpcError(e) && siteId) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_post_time_failed_not_found",
            message: "Failed to post time. Could not find the posted time group."
          }),
          messageType: "ERROR",
        })),
        // Timeline is probably out of sync.
        of(TimelineActions.onTimelineOutOfSync()),
        of(TimelineAdditionalUiSlice.resetIsPostingTime()),
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_post_time_failed_permission_denied",
            message: "Failed to post time. You do not have permission to post time."
          }),
          messageType: "ERROR",
        })),
        of(TimelineAdditionalUiSlice.resetIsPostingTime()),
      )
    }
    if (code === StatusCode.INVALID_ARGUMENT) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_post_time_failed_invalid_argument",
            message: "Failed to post time. Timesheet entry durations should be at least 30 seconds."
          }),
          messageType: "ERROR",
        })),
        of(TimelineAdditionalUiSlice.resetIsPostingTime()),
      )
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_post_time_failed_failed_precondition",
            message: "Failed to post time. The tags or timesheet fields may be out of sync."
          }),
          messageType: "ERROR",
        })),
        // Post time requirements and preferences for site may have changed.
        of(UserContextActions.refreshSiteDetails({ siteId })),
        // Timeline is probably out of sync.
        of(TimelineActions.onTimelineOutOfSync()),
        of(TimelineAdditionalUiSlice.resetIsPostingTime()),
      )
    }
  }

  return concat(
    of(showSnackBar({
      message: t({
        id: "msg_post_time_failed",
        message: "Failed to post time. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
      }),
      messageType: "ERROR",
    })),
    of(TimelineAdditionalUiSlice.resetIsPostingTime()),
  )
}

export default postTime(postTimeRpc)
